// UCC tab — manual servicing UCC-1 record (standard procedure when staff
// file directly with the filing office, without the Baselayer workspace).
// Posts /portfolio-admin/ucc/file-initial; debtor + secured party are
// seeded from the loan prefill when available, all fields stay editable.

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input, Label } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { uccPrefillApi } from "@/api/portfolioAdmin/uccPrefillApi";
import { portfolioAdminKeys } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import {
  invalidateLoanCalendar,
  invalidateLoanHealth,
} from "@/hooks/portfolioAdmin/invalidation";
import type { FileUcc1Request } from "@/api/portfolioAdmin/types";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

interface ManualFileUcc1ModalProps {
  loanId: string;
  open: boolean;
  onClose: () => void;
}

export function ManualFileUcc1Modal({
  loanId,
  open,
  onClose,
}: ManualFileUcc1ModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const prefillQuery = useQuery({
    queryKey: ["portfolio-admin", "ucc-debtor-prefill", loanId],
    queryFn: () => uccPrefillApi.getDebtorPrefill(loanId),
    enabled: open,
  });

  const fileInitial = useMutation({
    mutationFn: (body: FileUcc1Request) => uccPrefillApi.fileInitial(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: portfolioAdminKeys.uccFilings(loanId),
      });
      // Filing creates the lapse calendar items + flips the UCC health light.
      void invalidateLoanCalendar(queryClient, loanId);
      void invalidateLoanHealth(queryClient, loanId);
    },
  });

  const tz = useUserTimeZone();
  const today = isoDateInTz(new Date(), tz);
  const [filingNumber, setFilingNumber] = useState("");
  const [filingDate, setFilingDate] = useState(today);
  const [filingOffice, setFilingOffice] = useState("");
  const [filingJurisdiction, setFilingJurisdiction] = useState("");
  const [securedPartyLegalName, setSecuredPartyLegalName] = useState("");
  const [debtorLegalName, setDebtorLegalName] = useState("");
  const [debtorJurisdictionOfOrg, setDebtorJurisdictionOfOrg] = useState("");
  const [collateral, setCollateral] = useState("");
  const [isLongTerm, setIsLongTerm] = useState(false);

  // Seed names from the loan prefill while the fields are still empty.
  const prefill = prefillQuery.data;
  useEffect(() => {
    if (!open || !prefill) return;
    setDebtorLegalName((v) => v || prefill.debtorLegalName || "");
    setSecuredPartyLegalName((v) => v || prefill.securedPartyLegalName || "");
    setDebtorJurisdictionOfOrg((v) => v || prefill.debtorJurisdictionOfOrg || "");
    setFilingJurisdiction((v) => v || prefill.filingState || "");
  }, [open, prefill]);

  const onSubmit = async () => {
    if (
      !filingNumber.trim() ||
      !filingOffice.trim() ||
      !filingJurisdiction.trim() ||
      !securedPartyLegalName.trim() ||
      !debtorLegalName.trim()
    ) {
      toast.error(
        "Filing #, office, jurisdiction, secured party and debtor are required.",
      );
      return;
    }
    try {
      await fileInitial.mutateAsync({
        loanId,
        filingNumber: filingNumber.trim(),
        filingDate,
        filingOffice: filingOffice.trim(),
        filingJurisdiction: filingJurisdiction.trim(),
        isLongTerm,
        securedPartyLegalName: securedPartyLegalName.trim(),
        debtorLegalName: debtorLegalName.trim(),
        debtorJurisdictionOfOrg: debtorJurisdictionOfOrg.trim() || null,
        collateralDescriptionText: collateral.trim() || null,
      });
      toast.success("UCC-1 recorded.");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not record the UCC-1.",
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Record UCC-1 filing"
      description="Enter the UCC-1 as confirmed by the filing office."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={fileInitial.isPending}
            data-testid="manual-ucc1-submit"
          >
            Record filing
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-space-2" data-testid="manual-ucc1-form">
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="manual-ucc1-number" required>Filing #</Label>
          <Input
            id="manual-ucc1-number"
            value={filingNumber}
            onChange={(e) => setFilingNumber(e.target.value)}
            data-testid="manual-ucc1-number"
          />
        </div>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="manual-ucc1-date">Filing date</Label>
          <Input
            id="manual-ucc1-date"
            type="date"
            value={filingDate}
            onChange={(e) => setFilingDate(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="manual-ucc1-office" required>Filing office</Label>
          <Input
            id="manual-ucc1-office"
            value={filingOffice}
            onChange={(e) => setFilingOffice(e.target.value)}
            placeholder="Delaware SOS / …"
            data-testid="manual-ucc1-office"
          />
        </div>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="manual-ucc1-jurisdiction" required>Jurisdiction</Label>
          <Input
            id="manual-ucc1-jurisdiction"
            value={filingJurisdiction}
            onChange={(e) => setFilingJurisdiction(e.target.value)}
            placeholder="DE / CA / …"
            data-testid="manual-ucc1-jurisdiction"
          />
        </div>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="manual-ucc1-secured-party" required>Secured party</Label>
          <Input
            id="manual-ucc1-secured-party"
            value={securedPartyLegalName}
            onChange={(e) => setSecuredPartyLegalName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="manual-ucc1-debtor" required>Debtor legal name</Label>
          <Input
            id="manual-ucc1-debtor"
            value={debtorLegalName}
            onChange={(e) => setDebtorLegalName(e.target.value)}
            data-testid="manual-ucc1-debtor"
          />
        </div>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="manual-ucc1-debtor-org">Debtor org state</Label>
          <Input
            id="manual-ucc1-debtor-org"
            value={debtorJurisdictionOfOrg}
            onChange={(e) => setDebtorJurisdictionOfOrg(e.target.value)}
          />
        </div>
        <fieldset className="flex flex-col gap-space-1 border-0 p-0 m-0">
          <legend className="p-0 font-display text-label font-semibold uppercase tracking-label text-gray-mid mb-[6px]">
            Term
          </legend>
          <div className="flex items-center gap-space-3 flex-wrap">
            <label className="flex items-center gap-space-2 text-small text-onyx">
              <input
                type="radio"
                name="manual-ucc1-term"
                checked={!isLongTerm}
                onChange={() => setIsLongTerm(false)}
                className="h-4 w-4 accent-gold"
                data-testid="manual-ucc1-term-standard"
              />
              Standard (5-year)
            </label>
            <label className="flex items-center gap-space-2 text-small text-onyx">
              <input
                type="radio"
                name="manual-ucc1-term"
                checked={isLongTerm}
                onChange={() => setIsLongTerm(true)}
                className="h-4 w-4 accent-gold"
                data-testid="manual-ucc1-term-long"
              />
              Long-term (30-year)
            </label>
          </div>
        </fieldset>
        <div className="flex flex-col gap-space-1 col-span-2">
          <Label htmlFor="manual-ucc1-collateral">Collateral description</Label>
          <textarea
            id="manual-ucc1-collateral"
            rows={3}
            className="w-full px-space-3 py-[10px] font-body text-small text-onyx bg-white-pure leading-normal border border-border-light rounded-md placeholder:text-gray-mid focus:outline-none focus:border-gold focus:shadow-goldRing resize-y"
            value={collateral}
            onChange={(e) => setCollateral(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
